import { createContext } from 'react';

export type CommunicationAudienceOption = 'ALL' | 'TENANT' | 'OWNER' | 'SUPPLIER';

export type CommunicationMode = 'TEMPLATES' | 'NEW';

export type UploadedCommunicationFile = {
  id: string;
  fileName: string;
};

export type PropertySelectionMode = 'ALL' | 'SOME';

export type CreateCommunicationState = {
  title: string;
  message: string;
  targetUserTypes: CommunicationAudienceOption[];
  uploadedFiles: UploadedCommunicationFile[];
  selectedTemplateId: string | null;
  mode: CommunicationMode;
  propertySelectionMode: PropertySelectionMode;
  selectedPropertyIds: string[];
  /** Si hay propiedades seleccionadas: enviar a todos los vinculados (backend resuelve inquilino/dueño/proveedor) sin listas explícitas. */
  allStakeholdersOnSelectedProperties: boolean;
  selectedUserIds: string[];
  allUsersSelected: boolean;
  /** Solo inquilinos con contrato activo (lista explícita en cartera sin propiedades; con propiedades el backend acota por propiedad). */
  onlyActiveContractsSelected: boolean;
  /** IDs de usuario a excluir del conjunto resuelto por el backend. */
  excludeRecipientUserIds: string[];
};

export type CreateCommunicationContextActions = {
  state: CreateCommunicationState;
  setTitle: (title: string) => void;
  setMessage: (message: string) => void;
  toggleAudience: (option: CommunicationAudienceOption) => void;
  setTargetUserTypes: (types: CommunicationAudienceOption[]) => void;
  setMode: (mode: CommunicationMode) => void;
  setSelectedTemplate: (id: string | null) => void;
  addUploadedFiles: (files: UploadedCommunicationFile[]) => void;
  clearUploadedFiles: () => void;
  setPropertySelectionMode: (mode: PropertySelectionMode) => void;
  setSelectedPropertyIds: (ids: string[]) => void;
  setAllStakeholdersOnSelectedProperties: (value: boolean) => void;
  setSelectedUserIds: (ids: string[]) => void;
  toggleUserSelection: (userId: string) => void;
  setAllUsersSelected: (value: boolean) => void;
  setOnlyActiveContractsSelected: (value: boolean) => void;
  toggleExcludeRecipientUser: (userId: string) => void;
  setExcludeRecipientUserIds: (ids: string[]) => void;
  buttonState: boolean;
  setButtonDisabled: () => void;
  setButtonEnabled: () => void;
};

export type CreateCommunicationAction =
  | { type: 'CREATE_COMMUNICATION-setTitle'; payload: { title: string } }
  | { type: 'CREATE_COMMUNICATION-setMessage'; payload: { message: string } }
  | {
      type: 'CREATE_COMMUNICATION-toggleAudience';
      payload: { option: CommunicationAudienceOption };
    }
  | {
      type: 'CREATE_COMMUNICATION-setTargetUserTypes';
      payload: { types: CommunicationAudienceOption[] };
    }
  | { type: 'CREATE_COMMUNICATION-setMode'; payload: { mode: CommunicationMode } }
  | {
      type: 'CREATE_COMMUNICATION-setSelectedTemplate';
      payload: { id: string | null };
    }
  | {
      type: 'CREATE_COMMUNICATION-addUploadedFiles';
      payload: { files: UploadedCommunicationFile[] };
    }
  | { type: 'CREATE_COMMUNICATION-clearUploadedFiles' }
  | {
      type: 'CREATE_COMMUNICATION-setPropertySelectionMode';
      payload: { mode: PropertySelectionMode };
    }
  | {
      type: 'CREATE_COMMUNICATION-setSelectedPropertyIds';
      payload: { ids: string[] };
    }
  | {
      type: 'CREATE_COMMUNICATION-setSelectedUserIds';
      payload: { ids: string[] };
    }
  | {
      type: 'CREATE_COMMUNICATION-toggleUserSelection';
      payload: { userId: string };
    }
  | {
      type: 'CREATE_COMMUNICATION-setAllUsersSelected';
      payload: { value: boolean };
    }
  | {
      type: 'CREATE_COMMUNICATION-setOnlyActiveContractsSelected';
      payload: { value: boolean };
    }
  | {
      type: 'CREATE_COMMUNICATION-setAllStakeholdersOnSelectedProperties';
      payload: { value: boolean };
    }
  | {
      type: 'CREATE_COMMUNICATION-toggleExcludeRecipientUser';
      payload: { userId: string };
    }
  | {
      type: 'CREATE_COMMUNICATION-setExcludeRecipientUserIds';
      payload: { ids: string[] };
    };

export const CreateCommunicationContext =
  createContext<CreateCommunicationContextActions | null>(null);

